import * as yup from 'yup';
import {
  DocumentEntry,
  EducationValues,
  WorkExperienceValues,
} from '../types/employeeOnboarding';
import { FormValues } from '../types/employeeDir';
import { AddUserFormValues } from '../types/invite';
import { ContactFormValues } from 'types/crm/contacts';
import { DESCRIPTION_MAX } from 'types/crm/deal';
import { TaskFormValues } from 'types/crm/task';
import { NoteFormValues } from 'types/crm/notes';
import { BoardType } from 'types/createProject';

export const personalDetailsInitialValues = {
  firstName: '',
  lastName: '',
  email: '',
  contactNumber: '',
  dateOfBirth: '',
  gender: '',
  currentAddress: '',
  currentAddress2: '',
  middleName: '',
  country: '',
  city: '',
  postalCode: '',
  emergencyContactFullName: '',
  relationship: '',
  otherRelationship: '',
  emergencyPhoneNumber: '',
};

export const bankAccountInitialValues = {
  accountHolderName: '',
  bankName: '',
  accountNumber: '',
  confirmAccountNumber: '',
  ifscCode: '',
  accountType: '',
};

export const initialWorkExperience: WorkExperienceValues = {
  companyName: '',
  jobTitle: '',
  startDate: '',
  endDate: '',
  jobDescription: '',
};

export const initialEducationValues: EducationValues = {
  school: '',
  degree: '',
  fieldOfStudy: '',
  grade: '',
  startDate: '',
  endDate: '',
  activities: '',
  description: '',
  documentType: '',
  otherDocumentType: '',
  isCurrentlyStudying: false,
  mediaUuid: '',
  otherFieldOfStudy: '',
  otherDegreeQualification: '',
};

export const employeeInitialValues: FormValues = {
  fullName: '',
  employeeId: '',
  workEmail: '',
  phoneNumber: '',
  joiningDate: '',
  profilePhoto: null,
  department: '',
  role: '',
  reportingManager: '',
  workExperience: null,
  dateOfBirth: '',
  gender: '',
  address: '',
  idDocument: null,
  username: '',
  password: '',
  accessRoles: {
    hr: false,
    admin: false,
    finance: false,
  },
};

export const leaveRequestInitialValues = {
  leaveType: '',
  fromDate: '',
  toDate: '',
  reason: '',
};

export const companyInfoInitialValues = {
  companyName: '',
  country: '',
  timezone: '',
};

export const addUserInitialValues: AddUserFormValues = {
  email: '',
  hrmsAccess: 'None',
  projectManagementAccess: 'None',
  crmAccess: 'None',
  skipOnboarding: false,
  isOrgAdmin: false,
  projectUuidList: [],
};

export const sendNoteInitialValues = {
  date: new Date().toISOString().split('T')[0], // Today's date in YYYY-MM-DD format
  message: '',
};

export const addUserSchema = yup.object().shape({
  email: yup
    .string()
    .email('Invalid email format')
    .required('Work email is required'),
  hrmsAccess: yup.string().required('HRMS access level is required'),
  projectManagementAccess: yup
    .string()
    .required('Project Management access level is required'),
  crmAccess: yup.string().required('CRM access level is required'),
  isOrgAdmin: yup.boolean(),
  projectUuidList: yup.array().of(yup.string()).notRequired(),
});

export const resignationInitialValues = {
  noticeStartDate: new Date().toISOString().split('T')[0], // Today's date in YYYY-MM-DD format
  lastWorkingDay: '',
  reasonForResignation: '',
  detailedReason: '',
};

export const projectFormInitialValues = {
  projectName: '',
  selectedIcon: 'pIcon1',
  projectOwner: null,
  projectKey: '',
  projectType: '',
  boardType: '' as BoardType | '',
  startDate: '',
  endDate: '',
  teamMembers: [],
  software: '',
  projectDescription: '',
  projectVisibility: '',
};

export const resignationSchema = yup.object().shape({
  noticeStartDate: yup
    .string()
    .required('Notice start date is required')
    .test('is-valid-date', 'Invalid date format', function (value) {
      if (!value) return false;
      const date = new Date(value);
      return date instanceof Date && !isNaN(date.getTime());
    })
    .test(
      'is-not-past',
      'Notice start date cannot be in the past',
      function (value) {
        if (!value) return false;
        const selectedDate = new Date(value);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return selectedDate >= today;
      },
    ),

  lastWorkingDay: yup
    .string()
    .required('Last working day is required')
    .test('is-valid-date', 'Invalid date format', function (value) {
      if (!value) return false;
      const date = new Date(value);
      return date instanceof Date && !isNaN(date.getTime());
    })
    .test(
      'is-after-notice-start',
      'Last working day must be after notice start date',
      function (value) {
        const { noticeStartDate } = this.parent;
        if (!value || !noticeStartDate) return true;

        const lastWorkingDate = new Date(value);
        const noticeDate = new Date(noticeStartDate);
        return lastWorkingDate > noticeDate;
      },
    )
    .test(
      'minimum-notice-period',
      'Minimum 30 days notice period required',
      function (value) {
        const { noticeStartDate } = this.parent;
        if (!value || !noticeStartDate) return true;

        const lastWorkingDate = new Date(value);
        const noticeDate = new Date(noticeStartDate);
        const daysDifference = Math.ceil(
          (lastWorkingDate.getTime() - noticeDate.getTime()) /
            (1000 * 60 * 60 * 24),
        );

        return daysDifference >= 30;
      },
    ),

  reasonForResignation: yup
    .string()
    .required('Reason for resignation is required'),

  detailedReason: yup
    .string()
    .required('Detailed reason is required')
    .min(10, 'Please provide at least 10 characters for detailed reason')
    .max(500, 'Detailed reason cannot exceed 500 characters'),
});

export const initialDocumentValues: DocumentEntry = {
  id: '',
  name: '',
  idNumber: '',
  file: null,
  uploaded: false,
  size: '',
};

// Initial Values

export const identityDocumentSchema = yup.object().shape({
  name: yup.string().required('ID Name is required').trim(),
  otherName: yup.string().when('name', {
    is: 'Other',
    then: (schema) => schema.required('Please specify the ID Name').trim(),
    otherwise: (schema) => schema.nullable().notRequired(),
  }),
  idNumber: yup
    .string()
    .required('ID Number is required')
    .matches(/^[a-zA-Z0-9\s-]+$/, 'Please enter a valid ID number.')
    .trim(),
});

export const employeeFormSchema = yup.object({
  fullName: yup.string().required('Full Name is required'),
  employeeId: yup.string().required('Employee ID is required'),
  workEmail: yup
    .string()
    .email('Invalid email address')
    .required('Work Email is required'),
  phoneNumber: yup
    .string()
    .required('Phone Number is required')
    .min(6, 'Enter a valid phone number'),
  joiningDate: yup.string().required('Date of Joining is required'),
  department: yup.string().required('Department is required'),
  role: yup.string().required('Role is required'),
  reportingManager: yup.string().required('Reporting Manager is required'),
  dateOfBirth: yup.string().required('Date of Birth is required'),
  gender: yup.string().required('Gender is required'),
  address: yup.string().required('Address is required'),
  username: yup.string().required('Username is required'),
  password: yup.string().required('Password is required'),
});

export const personalDetailsSchema = yup.object().shape({
  firstName: yup
    .string()
    .required('First Name is required')
    .min(2, 'First Name must be at least 2 characters')
    .max(50, 'First Name cannot exceed 50 characters'),
  lastName: yup
    .string()
    .required('Last Name is required')
    .min(2, 'Last Name must be at least 2 characters')
    .max(50, 'Last Name cannot exceed 50 characters'),
  email: yup
    .string()
    .required('Email Address is required')
    .email('Invalid email address'),
  dateOfBirth: yup.string().required('Date of birth is required'),
  contactNumber: yup
    .string()
    .required('Phone Number is required')
    .min(6, 'Enter a valid phone number'),
  gender: yup.string().required('Gender is required'),
  currentAddress: yup.string().required('Current Address is required'),
  country: yup.string().required('Country is required'),
  city: yup.string().required('City is required'),
  postalCode: yup.string().required('Postal Code is required'),
  relationship: yup.string().required('Relationship is required'),
  otherRelationship: yup.string().when('relationship', {
    is: (val: string) => val === 'Other',
    then: (schema) => schema.required('Please specify the relationship'),
    otherwise: (schema) => schema.notRequired(),
  }),
  emergencyContactFullName: yup.string().required('Name is required'),
  emergencyPhoneNumber: yup
    .string()
    .required('Phone Number is required')
    .min(6, 'Enter a valid phone number'),
});

export const bankAccountSchema = yup.object().shape({
  accountHolderName: yup.string().required('Account holder name is required'),
  bankName: yup.string().required('Bank name is required'),
  accountNumber: yup.string().required('Account number is required'),
  confirmAccountNumber: yup
    .string()
    .required('Please confirm account number')
    .oneOf([yup.ref('accountNumber')], 'Account numbers must match'),
  ifscCode: yup.string().required('IFSC code is required'),
  accountType: yup.string().required('Account type is required'),
});

export const workExperienceSchema = yup.object().shape({
  companyName: yup.string().required('Company name is required'),
  jobTitle: yup.string().required('Job title is required'),
  startDate: yup
    .string()
    .required('Start date is required')
    .test('is-not-future', 'Start date cannot be in the future', (value) => {
      if (!value) return true;
      const date = new Date(value);
      const today = new Date();
      return date <= today;
    }),
  endDate: yup.string().required('End date is required'),
  jobDescription: yup.string().required('Job description is required'),
});

export const leaveRequestSchema = yup.object().shape({
  leaveType: yup.string().required('Leave type is required'),
  fromDate: yup.string().required('From date is required'),
  toDate: yup
    .string()
    .required('To date is required')
    .test('date-check', 'To date must be after from date', function (value) {
      const { fromDate } = this.parent;
      return !fromDate || !value || new Date(value) >= new Date(fromDate);
    }),
  reason: yup.string().required('Reason is required'),
});

export const companyInfoSchema = yup.object().shape({
  companyName: yup
    .string()
    .required('Company name is required')
    .matches(/[a-zA-Z]/, 'Must contain at least one letter'),
  country: yup.string().required('Country is required'),
  timezone: yup.string().required('Timezone is required'),
});

export const sendNoteSchema = yup.object().shape({
  date: yup.string().required('Date is required'),
  message: yup
    .string()
    .required('Message is required')
    .min(10, 'Message must be at least 10 characters'),
});

export const educationSchema = yup.object().shape({
  school: yup.string().required('School is required'),
  degree: yup.string().required('Degree is required'),
  fieldOfStudy: yup.string().required('Field of study is required'),
  grade: yup
    .string()
    .nullable()
    .test(
      'is-numeric',
      'Grade / GPA must be a valid numeric value (e.g. 8.5, 90, 3.75)',
      (value) => {
        if (!value || value.trim() === '') return true; // optional
        return /^\d+(\.\d+)?$/.test(value.trim());
      },
    ),
  startDate: yup
    .string()
    .required('Start date is required')
    .test('is-not-future', 'Start date cannot be in the future', (value) => {
      if (!value) return true;
      const date = new Date(value);
      const today = new Date();
      return date <= today;
    }),
  isCurrentlyStudying: yup.boolean(),
  endDate: yup.string().when('isCurrentlyStudying', {
    is: (val: boolean) => val === false || val === undefined,
    then: (schema) =>
      schema.required('End date is required when not currently studying'),
    otherwise: (schema) => schema.notRequired(),
  }),
  activities: yup.string().nullable(), // optional
  description: yup.string().nullable(), // optional
  documentType: yup.string().required('Document type is required'),
  otherDocumentType: yup.string().when('documentType', {
    is: (val: string) => val === 'Other',
    then: (schema) => schema.required('Please specify document type'),
    otherwise: (schema) => schema.notRequired(),
  }),
  otherDegreeQualification: yup.string().when('degree', {
    is: (val: string) => val === 'OTHER' || val === 'Other',
    then: (schema) => schema.required('Please specify degree / qualification'),
    otherwise: (schema) => schema.notRequired(),
  }),
  otherFieldOfStudy: yup.string().when('fieldOfStudy', {
    is: (val: string) => val === 'OTHER' || val === 'Other',
    then: (schema) => schema.required('Please specify field of study'),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const maxWords = (limit: number, message?: string) =>
  yup
    .string()
    .test('max-words', message || `Cannot exceed ${limit} words`, (value) => {
      if (!value) return true;
      const words = value.trim().split(/\s+/).filter(Boolean);
      return words.length <= limit;
    });

export const projectFormValidationSchema = yup.object({
  projectName: yup
    .string()
    .max(20, 'Project name must be at most 20 characters')
    .required('Project name is required'),
  selectedIcon: yup.string().required('Project icon is required'),
  projectOwner: yup.object().nullable().required('Project owner is required'),
  // projectKey: yup.string().required('Project Key is required'),
  // projectType: yup.string().required('Project Type is required'),
  boardType: yup
    .string()
    .oneOf(['KANBAN', 'SPRINT'], 'Select a valid board type')
    .required('Board type is required'),
  startDate: yup.string().required('Start date is required'),
  endDate: yup.string(),
  teamMembers: yup.array().min(0, 'Team members must be an array'),
  projectDescription: maxWords(
    200,
    'Project description cannot exceed 200 words.',
  ).optional(),
  // projectVisibility: yup
  //   .string()
  //   .oneOf(
  //     ['Private', 'Public'],
  //     'Project visibility must be either Private or Public'
  //   )
  //   .required('Project visibility is required'),
});

const passwordRule = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d).{5,}$/;
//Password must be at least 5 characters and include uppercase, lowercase, and a number

export const SignupFormInitialValues = {
  email: '',
  password: '',
  confirmPassword: '',
  firstName: '',
  lastName: '',
};

const baseSignupSchema = {
  firstName: yup
    .string()
    .required('First name is required')
    .min(1, 'First name must be at least 1 characters')
    .matches(/[a-zA-Z]/, 'Must contain at least one letter'),

  lastName: yup
    .string()
    .required('Last name is required')
    .min(1, 'First name must be at least 1 characters')
    .matches(/[a-zA-Z]/, 'Must contain at least one letter'),

  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(
      passwordRule,
      'Password must include uppercase, lowercase, number, and special character',
    ),

  confirmPassword: yup
    .string()
    .required('Please confirm your password')
    .oneOf([yup.ref('password')], 'Passwords do not match'),
};

export const InvitedSignupFormValidationSchema = yup.object({
  ...baseSignupSchema,
});

export const SignupFormValidationSchema = yup.object({
  email: yup
    .string()
    .required('Email is required')
    .matches(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/, 'Please enter a valid email'),
  ...baseSignupSchema,
});

export const LoginFormInitialValues = {
  password: '',
  email: '',
};

export const LoginFormValidationSchema = yup.object({
  email: yup
    .string()
    .email('Please enter a valid email')
    .required('Email is required'),
  password: yup.string().required('Password is required'),
});
// forgot password validation

export const forgotPasswordInitialValues = { email: '' };
export const forgotPasswordSchema = yup.object({
  email: yup
    .string()
    .email('Please enter a valid email')
    .required('Email is required'),
});

// reset password validation

export const resetPasswordInitialValues = { password: '', confirmPassword: '' };

export const resetPasswordSchema = yup.object({
  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(
      passwordRule,
      'Password must include uppercase, lowercase, number, and special character',
    ),
  confirmPassword: yup
    .string()
    .required('Please confirm your password')
    .oneOf([yup.ref('password')], 'Passwords do not match'),
});

export const profileSchema = (isGoogleUser: boolean) =>
  yup.object({
    firstName: isGoogleUser
      ? yup.string()
      : yup.string().trim().required('First name is required'),
    lastName: isGoogleUser
      ? yup.string()
      : yup.string().trim().required('Last name is required'),
    contactNumber: yup.string().nullable().min(6, 'Enter a valid phone number'),
  });

// CRM Contacts Validation

export const contactFormInitialValues: ContactFormValues = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  jobTitle: '',
  profileImageURL: '',
  companyName: '',
  companyReference: '',
  companyWebsiteUrl: '',
  linkedInUrl: '',
  lifecycleStage: '',
  engagementStatus: '',
  contactOwnerUuid: '',
  tags: [],
  notes: '',
};

// Both fields show a scheme-less placeholder ("linkedin.com/in/...", "company.com"),
// so `yup.url()` is too strict here - it rejects anything without http(s)://.
const LINKEDIN_URL_PATTERN =
  /^(https?:\/\/)?([a-z0-9-]+\.)*linkedin\.com(\/\S*)?$/i;
const WEBSITE_URL_PATTERN = /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}(\/\S*)?$/i;

export const contactFormValidationSchema = yup.object({
  // Names carry digits, spaces and punctuation in the real world — initials,
  // suffixes, company-shaped names, transliterations. Only "present" and "not
  // too long" are checked; what the name is made of is not ours to rule on.
  firstName: yup
    .string()
    .trim()
    .required('First name is required')
    .max(40, 'First name must be at most 40 characters'),

  lastName: yup
    .string()
    .trim()
    .max(40, 'Last name must be at most 40 characters'),
  email: yup
    .string()
    .trim()
    .email('Enter a valid email')
    .required('Email is required'),
  phone: yup.string().nullable().min(6, 'Enter a valid phone number'),
  jobTitle: yup
    .string()
    .nullable()
    .required('Job title is required')
    .max(40, 'Job Title must be at most 40 characters'),
  companyName: yup.string().trim().required('Company name is required'),
  linkedInUrl: yup
    .string()
    .nullable()
    .max(200, 'LinkedIn must be at most 200 characters')
    .test(
      'linkedin-url',
      'Enter a valid LinkedIn URL',
      (value) => !value || LINKEDIN_URL_PATTERN.test(value.trim()),
    ),
  companyWebsiteUrl: yup
    .string()
    .nullable()
    .max(200, 'Company website must be at most 200 characters')
    .test(
      'company-website-url',
      'Enter a valid website (e.g. company.com)',
      (value) => !value || WEBSITE_URL_PATTERN.test(value.trim()),
    ),
  // No `oneOf`: both are configured per organisation in Settings → CRM Values,
  // so the valid set is whatever `…/assignable` returned when the form opened —
  // not something this schema can be built against. The dropdown only offers
  // Active values, and the server is the one that rules on the rest.
  lifecycleStage: yup.string(),
  engagementStatus: yup.string(),
  contactOwnerUuid: yup.string().required('Owner is required'),
  tags: yup.array().of(yup.string()),
  notes: yup
    .string()
    .trim()
    .max(500, 'Insights must be at most 500 characters'),
});

/**
 * What the inline Edit Contact form checks.
 *
 * Only the two optional URLs, taken from the create schema so the two forms
 * cannot drift apart. The rest of that schema is deliberately left out: it
 * requires fields the inline editor has always allowed through, and turning
 * that on here would reject edits that save fine today.
 */
export const contactEditValidationSchema = contactFormValidationSchema.pick([
  'linkedInUrl',
  'companyWebsiteUrl',
]);

export const dealsValidationSchema = yup.object().shape({
  name: yup
    .string()
    .trim()
    .required('Deal name is required')
    .max(120, 'Deal name must be at most 120 characters'),
  pipeline: yup.string().trim().required('Pipeline is required'),
  dealStage: yup.string().trim().required('Deal stage is required'),
  currency: yup.string().trim(),
  amount: yup
    .number()
    .typeError('Enter a valid amount')
    .min(0, 'Amount must be positive')
    .nullable()
    .transform((v, o) => (o === '' ? null : v))
    .required('Anount is required'),
  closeDate: yup.string().trim(),
  owner: yup.string().trim(),
  dealType: yup.string().trim(),
  priority: yup.string().trim(),
  description: yup
    .string()
    .trim()
    .max(
      DESCRIPTION_MAX,
      `Insights must be at most ${DESCRIPTION_MAX} characters`,
    ),
});

export const taskFormInitialValues: TaskFormValues = {
  title: '',
  description: '',
  taskType: '',
  priority: '',
  taskOwnerUuid: '',
  dueAt: '',
  taskStatus: '',
  relatedTo: '',
};

export const taskFormValidationSchema = yup.object({
  title: yup.string().trim().required('Title is required'),
  description: yup
    .string()
    .trim()
    .max(500, 'Insights must be at most 500 characters'),
  taskType: yup.string().required('Task type is required'),
  priority: yup.string().required('Priority is required'),
  taskOwnerUuid: yup.string().nullable(),
  dueAt: yup.string(),
  taskStatus: yup.string().required('Status is required'),
  relatedTo: yup.string().nullable(),
});

export const noteFormInitialValues: NoteFormValues = {
  note: '',
  associatedPersonUuid: '',
};

export const noteFormValidationSchema = yup.object({
  note: yup.string().trim().required('Note is required'),
  associatedPersonUuid: yup.string().nullable(),
});
export const companyValidationSchema = yup.object().shape({
  title: yup
    .string()
    .trim()
    .required('Company name is required')
    .max(100, 'Company name must be at most 100 characters'),
  industry: yup.string().trim(),
  city: yup.string().trim().max(100, 'Location must be at most 100 characters'),
  website: yup
    .string()
    .trim()
    .url('Enter a valid URL')
    .max(200, 'Website must be at most 200 characters'),
  phone: yup.string().trim().nullable().min(6, 'Enter a valid phone number'),
  owner: yup.string().trim(),
  linkedin: yup
    .string()
    .trim()
    .url('Enter a valid URL')
    .max(200, 'LinkedIn must be at most 200 characters'),
  tags: yup.array().of(yup.string()),
  description: yup
    .string()
    .trim()
    .max(500, 'Insights must be at most 500 characters'),
});

export const orgSettingsInitialValues = {
  name: '',
  country: '',
  timezone: '',
};

export const orgSettingsValidationSchema = yup.object().shape({
  name: yup.string().trim().required('Please enter an organization name.'),
  country: yup.string().required('Please select a country.'),
  timezone: yup.string().required('Please select an organization timezone.'),
});
